import { useCallback, useEffect, useMemo, useState } from "react";
import { Formik, Form, getIn } from "formik";
import * as Yup from "yup";
import Drawer from "../../../SideDrawer/SideDrawer";
import Input from "../../../Input/Input";
import Button from "../../../Button/Button";
import Dropdown from "../../../Dropdown/Dropdown";
import MultiSelectSearchableDropdown from "../../../MultiSelectSearchableDropdown/MultiSelectSearchableDropdown";
import SearchableDropdown from "../../../SearchableDropdown/SearchableDropdown";
import PhoneNumberCountryCodeSelector from "../../../PhoneNumberCountryCodeSelector/PhoneNumberCountryCodeSelector";
import {
  transformCountryOptions,
  sortCountryOptions,
  formatPhoneNumber,
} from "../../../PhoneNumberCountryCodeSelector/utils/phoneNumberUtils";
import { countryOptions } from "../../../../utils/constants/countryOptions";
import { CountryOption } from "../../../PhoneNumberCountryCodeSelector/utils/types";
import SingleDatePicker from "../../../SingleDatePicker/SingleDatePicker";
import TimePicker from "../../../TimePicker/TimePicker";
import CalendarIcon from "../../../../assets/svg/calendar-icon";
import ClockIcon from "../../../../assets/svg/clock-icon";
import NetworkIcon from "../../../../assets/svg/network-icon";
import CloseIcon from "../../../../assets/svg/close-icon";
import { timezoneOptions } from "../../../../utils/constants/timezoneOptions";
import { convertFromTimezoneToUtc } from "../../../../utils/functions/convert-to-local-timezone";
import { toZonedTime, format as tzFormat } from "date-fns-tz";
import { formatAsLocalTime } from "../../../../utils/functions/date-time";
import InputWithDropdown from "../../../InputWithDropdown/InputWithDropdown";
import {
  useGetLeadCountriesQuery,
  useGetLeadCitiesQuery,
} from "../../../../redux/services/communicationApi";
import AgentDropdown from "../../../AgentDropdown/AgentDropdown";
import { skipToken } from "@reduxjs/toolkit/query";
import { Country, City } from "../../../../pages/Crm/utils/types";
import AddUserIcon from "../../../../assets/svg/add-user-icon";
import EditUserIcon from "../../../../assets/svg/edit-user-icon";

export interface VfLeadFormValues {
  name: string;
  email: string;
  phoneCountry: CountryOption | null;
  phoneNumber: string;
  job_title: string;
  company_name: string;
  industry: string;
  how_can_verbaflo_help: string;
  number_of_beds_managed: string;
  number_of_properties: string;
  asset_type: string[];
  city: string[];
  city_names: string[];
  country: string;
  country_name: string;
  office_location: string;
  crm_currently_used: string;
  pms_currently_used: string;
  voip: string;
  demo_1_date: string;
  demo_1_time: string;
  demo_2_date: string;
  demo_2_time: string;
  demo_3_date: string;
  demo_3_time: string;
  demo_4_date: string;
  demo_4_time: string;
  timezone: string;
  priority: string;
  contact_person_name: string;
  contact_person_email: string;
  contact_person_designation: string;
  website: string;
  agent_id: string[];
  contract_link: string;
  lead_source: string;
  fathom_link: string;
}

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Name is required"),
  email: Yup.string().email("Invalid email").required("Email is required"),
  phoneNumber: Yup.string().required("Phone number is required"),
  company_name: Yup.string().required("Company name is required"),
  industry: Yup.string().required("Please select an industry"),
  number_of_beds_managed: Yup.string().required("Beds managed is required"),
  country: Yup.string().required("Country is required"),
  priority: Yup.string().required("Priority is required"),
  contact_person_email: Yup.string().email("Invalid email"),
  website: Yup.string().url("Invalid URL").required("Website is required"),
  agent_id: Yup.array()
    .of(Yup.string())
    .min(1, "At least one assignee is required"),
});

const defaultInitialValues: VfLeadFormValues = {
  name: "",
  email: "",
  phoneCountry: null,
  phoneNumber: "",
  job_title: "",
  company_name: "",
  industry: "",
  how_can_verbaflo_help: "",
  number_of_beds_managed: "",
  number_of_properties: "",
  asset_type: [],
  city: [],
  city_names: [],
  country: "",
  country_name: "",
  office_location: "",
  crm_currently_used: "",
  pms_currently_used: "",
  voip: "",
  demo_1_date: "",
  demo_1_time: "",
  demo_2_date: "",
  demo_2_time: "",
  demo_3_date: "",
  demo_3_time: "",
  demo_4_date: "",
  demo_4_time: "",
  timezone: "",
  priority: "",
  contact_person_name: "",
  contact_person_email: "",
  contact_person_designation: "",
  website: "",
  agent_id: [],
  contract_link: "",
  lead_source: "",
  fathom_link: "",
};

const INDUSTRY_OPTIONS = [
  { label: "Student Housing", value: "student_housing" },
  { label: "BTR", value: "btr" },
  { label: "STR", value: "str" },
  { label: "HMO", value: "hmo" },
  { label: "Commercial", value: "commercial" },
];

const ASSET_TYPE_OPTIONS = [
  { label: "Co-living", value: "co-living" },
  { label: "Student Housing", value: "student-housing" },
  { label: "PG", value: "pg" },
  { label: "Hotel", value: "hotel" },
  { label: "Serviced Apartments", value: "serviced-apartments" },
];

const BEDS_MANAGED_OPTIONS = [
  { value: "1-50", content: "1 - 50" },
  { value: "51-200", content: "51 - 200" },
  { value: "201-500", content: "201 - 500" },
  { value: "501-1000", content: "501 - 1,000" },
  { value: "1001-5000", content: "1,001 - 5,000" },
  { value: "5001-10000", content: "5,001 - 10,000" },
  { value: "10001-50000", content: "10,001 - 50,000" },
  { value: "50001-100000", content: "50,001 - 1,00,000" },
  { value: "100001-500000", content: "1,00,001 - 5,00,000" },
  { value: "500001-10000000", content: "5,00,001 - 1,00,00,000" },
];

const PROPERTIES_OPTIONS = [
  { value: "1-5", content: "1 - 5" },
  { value: "6-20", content: "6 - 20" },
  { value: "21-50", content: "21 - 50" },
  { value: "51-100", content: "51 - 100" },
  { value: "101-500", content: "101 - 500" },
  { value: "501-1000", content: "501 - 1,000" },
  { value: "1001-5000", content: "1,001 - 5,000" },
  { value: "5001-10000", content: "5,001 - 10,000" },
  { value: "10001-50000", content: "10,001 - 50,000" },
  { value: "50001-100000", content: "50,001 - 1,00,000" },
];

const VF_PMS_OPTIONS = [
  { value: "Bunk", content: "Bunk" },
  { value: "Concurrent", content: "Concurrent" },
  { value: "Entrata", content: "Entrata" },
  { value: "Entrata US", content: "Entrata US" },
  { value: "HFSApiClient", content: "HFSApiClient" },
  { value: "Kinetics", content: "Kinetics" },
  { value: "Londonist", content: "Londonist" },
  { value: "LumisLiving", content: "LumisLiving" },
  { value: "MRI", content: "MRI" },
  { value: "ModaMRIClient", content: "ModaMRIClient" },
  { value: "Rezpage", content: "Rezpage" },
  { value: "Starrez", content: "Starrez" },
  { value: "Street", content: "Street" },
  { value: "Vita Algolia", content: "Vita Algolia" },
  { value: "Yugo", content: "Yugo" },
];

const PRIORITY_OPTIONS = [
  { value: "high", content: "High" },
  { value: "medium", content: "Medium" },
  { value: "low", content: "Low" },
];

const inputContainerClass =
  "border border-border-gray dark:border-border-gray-dk rounded-lg";
const inputClass = "w-full p-2";
const labelClass =
  "text-md text-verba-field-label dark:text-verba-field-label-dk";
const errorClass =
  "text-xs text-verba-red-alert dark:text-verba-red-alert-dk mt-0.5";

const PmsOtherHeader = ({
  isOtherPms,
  setIsOtherPms,
  customPmsValue,
  setCustomPmsValue,
  onSubmit,
}: {
  isOtherPms: boolean;
  setIsOtherPms: (v: boolean) => void;
  customPmsValue: string;
  setCustomPmsValue: (v: string) => void;
  onSubmit: (value: string) => void;
}) => {
  useEffect(() => {
    return () => {
      setIsOtherPms(false);
      setCustomPmsValue("");
    };
  }, [setIsOtherPms, setCustomPmsValue]);

  const handleOtherClick = () => setIsOtherPms(true);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setCustomPmsValue(e.target.value);

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) =>
    e.stopPropagation();

  const handleContainerClick = (e: React.MouseEvent<HTMLDivElement>) =>
    e.stopPropagation();

  const handleSubmitClick = () => onSubmit(customPmsValue.trim());

  return (
    <div className="px-[14px] py-[10px] border-b border-border-gray dark:border-border-gray-dk">
      {!isOtherPms ? (
        <Button
          type="button"
          onClick={handleOtherClick}
          innerText="Other"
          buttonContainerClass="text-md text-blue-600 dark:text-blue-400 hover:underline p-0"
          innerTextClass="text-md font-normal"
        />
      ) : (
        <div
          className="flex items-center gap-2"
          onClick={handleContainerClick}
        >
          <input
            type="text"
            value={customPmsValue}
            onChange={handleInputChange}
            placeholder="Enter PMS name"
            className="flex-1 min-w-0 px-2 py-1 text-md rounded border border-border-gray dark:border-border-gray-dk bg-pure-white dark:bg-pure-white-dk text-heading-text dark:text-heading-text-dk outline-none"
            autoFocus
            onKeyDown={handleInputKeyDown}
          />
          <Button
            type="button"
            disabled={!customPmsValue.trim()}
            onClick={handleSubmitClick}
            innerText="Submit"
            buttonContainerClass="shrink-0 px-3 py-1 rounded bg-verba-blue dark:bg-verba-blue-dk disabled:opacity-50"
            innerTextClass="text-md font-medium text-pure-white"
          />
        </div>
      )}
    </div>
  );
};

const rangeToString = (obj: unknown): string => {
  if (!obj || typeof obj !== "object") return "";
  const { min, max } = obj as { min?: number; max?: number };
  if (min == null || max == null) return "";
  return `${min}-${max}`;
};

const normalizeRangeValue = (val: string): string => {
  if (!val) return "";
  const parts = val.split("-");
  if (parts.length === 2 && parts[0] === parts[1]) return parts[0];
  return val;
};

const getDateTimeInTimezone = (
  utcString: string,
  timezone: string
): { date: string; time: string } => {
  if (!utcString) return { date: "", time: "" };
  try {
    const zonedDate = timezone
      ? toZonedTime(new Date(utcString), timezone)
      : new Date(utcString);
    return {
      date: tzFormat(zonedDate, "yyyy-MM-dd", timezone ? { timeZone: timezone } : undefined),
      time: tzFormat(zonedDate, "HH:mm", timezone ? { timeZone: timezone } : undefined),
    };
  } catch {
    return {
      date: utcString.slice(0, 10),
      time: utcString.slice(11, 16),
    };
  }
};

const buildEditInitialValues = (
  lead: Record<string, unknown>,
  phoneCountryOptions: CountryOption[]
): VfLeadFormValues => {
  const phoneDialCode = (lead.phone_country_code as string) || "";
  const matchedCountry = phoneDialCode
    ? (phoneCountryOptions.find((c) => c.dialCode === phoneDialCode) ?? null)
    : null;

  const demographics = (lead.demographics as Record<string, unknown>) || {};
  const destCountry = (demographics.destination_country as string) || "";
  const destCountryId = demographics.destination_country_id?.toString() || "";
  const destCity = (demographics.destination_city as string) || "";
  const destCityId = demographics.destination_city_id?.toString() || "";
  const timezone = (demographics.timezone as string) || "";

  const demo1 = getDateTimeInTimezone(lead.demo_1_date as string, timezone);
  const demo2 = getDateTimeInTimezone(lead.demo_2_date as string, timezone);
  const demo3 = getDateTimeInTimezone(lead.demo_3_date as string, timezone);
  const demo4 = getDateTimeInTimezone(lead.demo_4_date as string, timezone);

  return {
    name: (lead.name as string) || "",
    email: (lead.email as string) || "",
    phoneCountry: matchedCountry,
    phoneNumber: (lead.phone as string) || "",
    job_title: (lead.job_title as string) || "",
    company_name: (lead.company_name as string) || "",
    industry: (lead.industry as string) || "",
    how_can_verbaflo_help: (lead.comment as string) || "",
    number_of_beds_managed: normalizeRangeValue(
      rangeToString(lead.number_of_beds)
    ),
    number_of_properties: normalizeRangeValue(rangeToString(lead.number_of_properties)),
    asset_type: [],
    city: destCityId ? [destCityId] : [],
    city_names: destCity ? [destCity] : [],
    country: destCountryId,
    country_name: destCountry,
    office_location: (lead.office_location as string) || "",
    crm_currently_used: (lead.external_crm_name as string) || "",
    pms_currently_used: (lead.external_pms_name as string) || "",
    voip: (lead.external_voip_name as string) || "",
    demo_1_date: demo1.date,
    demo_1_time: demo1.time,
    demo_2_date: demo2.date,
    demo_2_time: demo2.time,
    demo_3_date: demo3.date,
    demo_3_time: demo3.time,
    demo_4_date: demo4.date,
    demo_4_time: demo4.time,
    timezone,
    priority: (lead.verbaflo_request_priority as string) || "",
    contact_person_name: (lead.contact_person_name as string) || "",
    contact_person_email: (lead.contact_person_email as string) || "",
    contact_person_designation:
      (lead.contact_person_designation as string) || "",
    website: (lead.website_url as string) || "",
    agent_id: Array.isArray(lead.agent)
      ? (lead.agent as { id: string }[]).map((a) => a.id)
      : [],
    contract_link: (lead.contract_link as string) || "",
    lead_source: (lead.lead_source as string) || "",
    fathom_link: (lead.fathom_link as string) || "",
  };
};

const VfCreateLeadForm = ({
  openDrawer,
  handleCloseDrawer,
  onSubmit,
  isUpdating,
  isEdit = false,
  leadDetails,
}: {
  openDrawer: boolean;
  handleCloseDrawer: () => void;
  onSubmit: (values: VfLeadFormValues) => void;
  isUpdating: boolean;
  isEdit?: boolean;
  leadDetails?: Record<string, unknown>;
}) => {
  const { data: countryData, isLoading: isCountryLoading } =
    useGetLeadCountriesQuery(openDrawer ? {} : skipToken);

  const destinationCountryOptions = useMemo((): {
    value: string;
    content: React.ReactNode;
    label: string;
  }[] => {
    if (!countryData) return [];
    return countryData.map((c: Country) => ({
      value: c.id.toString(),
      content: (
        <span className="capitalize text-md font-normal text-heading-text dark:text-heading-text-dk">
          {c.name}
        </span>
      ),
      label: c.name,
    }));
  }, [countryData]);

  const transformedPhoneCountryOptions =
    transformCountryOptions(countryOptions);
  const sortedPhoneCountryOptions = sortCountryOptions(
    transformedPhoneCountryOptions
  );

  const editInitialValues = useMemo(() => {
    if (!isEdit || !leadDetails) return defaultInitialValues;
    const vals = buildEditInitialValues(leadDetails, sortedPhoneCountryOptions);
    if (
      !vals.country &&
      destinationCountryOptions.length > 0 &&
      vals.country_name
    ) {
      const match = destinationCountryOptions.find(
        (o) => o.label.toLowerCase() === vals.country_name.toLowerCase()
      );
      if (match) vals.country = match.value;
    }
    return vals;
  }, [
    isEdit,
    leadDetails,
    sortedPhoneCountryOptions,
    destinationCountryOptions,
  ]);

  const formInitialValues = isEdit ? editInitialValues : defaultInitialValues;

  const shouldShowError = (
    touched: Record<string, unknown>,
    errors: Record<string, unknown>,
    submitCount: number,
    fieldPath: string
  ) =>
    (getIn(touched, fieldPath) || submitCount > 0) &&
    Boolean(getIn(errors, fieldPath));

  const getErrorMessage = (
    touched: Record<string, unknown>,
    errors: Record<string, unknown>,
    submitCount: number,
    fieldPath: string
  ) =>
    shouldShowError(touched, errors, submitCount, fieldPath)
      ? (getIn(errors, fieldPath) as string)
      : "";

  return (
    <Drawer
      open={openDrawer}
      onClose={handleCloseDrawer}
      direction="right"
      size="420px"
      overlayColor="rgba(0,0,0,0.7)"
      duration={300}
      closeOnOverlayClick={true}
      closeOnEscape={true}
      drawerContainerClassName="rounded-l-[8px]"
    >
      {openDrawer && (
        <Formik<VfLeadFormValues>
          initialValues={formInitialValues}
          validationSchema={validationSchema}
          enableReinitialize={isEdit}
          onSubmit={(values) => {
            const demoFields = [
              { date: "demo_1_date", time: "demo_1_time" },
              { date: "demo_2_date", time: "demo_2_time" },
              { date: "demo_3_date", time: "demo_3_time" },
              { date: "demo_4_date", time: "demo_4_time" },
            ] as const;
            const converted = { ...values };
            for (const { date, time } of demoFields) {
              if (converted[date] && converted[time] && converted.timezone) {
                const localTimestamp = `${converted[date]}T${converted[time]}:00`;
                const utc = convertFromTimezoneToUtc(localTimestamp, converted.timezone);
                if (utc) {
                  (converted as Record<string, unknown>)[date] = utc;
                }
              }
            }
            onSubmit(converted);
          }}
        >
          {({
            values,
            errors,
            touched,
            submitCount,
            handleChange,
            handleBlur,
            setFieldValue,
            setFieldTouched,
            isSubmitting,
            resetForm,
          }) => {
            const showErr = (field: string) =>
              shouldShowError(touched, errors, submitCount, field);
            const errMsg = (field: string) =>
              getErrorMessage(touched, errors, submitCount, field);

            return (
              <FormContent
                values={values}
                errors={errors}
                touched={touched}
                submitCount={submitCount}
                handleChange={handleChange}
                handleBlur={handleBlur}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                isSubmitting={isSubmitting}
                resetForm={resetForm}
                isUpdating={isUpdating}
                handleCloseDrawer={handleCloseDrawer}
                showErr={showErr}
                errMsg={errMsg}
                destinationCountryOptions={destinationCountryOptions}
                isCountryLoading={isCountryLoading}
                openDrawer={openDrawer}
                sortedPhoneCountryOptions={sortedPhoneCountryOptions}
                isEdit={isEdit}
                editInitialValues={formInitialValues}
              />
            );
          }}
        </Formik>
      )}
    </Drawer>
  );
};

const FormContent = ({
  values,
  handleChange,
  handleBlur,
  setFieldValue,
  setFieldTouched,
  isSubmitting,
  resetForm,
  isUpdating,
  handleCloseDrawer,
  showErr,
  errMsg,
  destinationCountryOptions,
  isCountryLoading,
  openDrawer,
  sortedPhoneCountryOptions,
  isEdit = false,
  editInitialValues,
}: {
  values: VfLeadFormValues;
  errors: Record<string, unknown>;
  touched: Record<string, unknown>;
  submitCount: number;
  handleChange: (e: React.ChangeEvent<unknown>) => void;
  handleBlur: (e: React.FocusEvent<unknown>) => void;
  setFieldValue: (
    field: string,
    value: unknown,
    shouldValidate?: boolean
  ) => void;
  setFieldTouched: (
    field: string,
    isTouched?: boolean,
    shouldValidate?: boolean
  ) => void;
  isSubmitting: boolean;
  resetForm: () => void;
  isUpdating: boolean;
  handleCloseDrawer: () => void;
  showErr: (field: string) => boolean;
  errMsg: (field: string) => string;
  destinationCountryOptions: {
    value: string;
    content: React.ReactNode;
    label: string;
  }[];
  isCountryLoading: boolean;
  openDrawer: boolean;
  sortedPhoneCountryOptions: CountryOption[];
  isEdit?: boolean;
  editInitialValues?: VfLeadFormValues;
}) => {
  const { data: cityData, isLoading: isCityLoading } = useGetLeadCitiesQuery(
    openDrawer && values.country ? values.country : skipToken
  );

  const cityOptions = useMemo(() => {
    if (!cityData) return [];
    return cityData.map((c: City) => ({
      value: c.id.toString(),
      label: c.name,
      content: (
        <span className="capitalize text-md font-normal text-heading-text dark:text-heading-text-dk">
          {c.name}
        </span>
      ),
    }));
  }, [cityData]);

  const initialPhone =
    isEdit && editInitialValues?.phoneNumber
      ? formatPhoneNumber(
          editInitialValues.phoneNumber,
          editInitialValues.phoneCountry
        )
      : "";
  const [phoneInput, setPhoneInput] = useState(initialPhone);

  const [isOtherPms, setIsOtherPms] = useState(false);
  const [customPmsValue, setCustomPmsValue] = useState("");

  const pmsOptions = useMemo(() => {
    const currentVal = values.pms_currently_used;
    if (currentVal && !VF_PMS_OPTIONS.some((opt) => opt.value === currentVal)) {
      return [...VF_PMS_OPTIONS, { value: currentVal, content: currentVal }];
    }
    return VF_PMS_OPTIONS;
  }, [values.pms_currently_used]);

  const handleCustomPmsSubmit = (value: string) => {
    setFieldValue("pms_currently_used", value);
    setIsOtherPms(false);
    setCustomPmsValue("");
    document.getElementById("pms-currently-used-dropdown-trigger")?.click();
  };

  const handlePhoneInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digitsOnly = e.target.value.replace(/\D/g, "");
    const formattedNumber = formatPhoneNumber(digitsOnly, values.phoneCountry);
    setPhoneInput(formattedNumber);
    setFieldValue("phoneNumber", digitsOnly);
  };

  const handlePhoneCountryChange = (country: CountryOption) => {
    setFieldValue("phoneCountry", country);
    if (phoneInput) {
      const digitsOnly = phoneInput.replace(/\D/g, "");
      const formattedNumber = formatPhoneNumber(digitsOnly, country);
      setPhoneInput(formattedNumber);
      setFieldValue("phoneNumber", digitsOnly);
    }
  };

  const hasChanges = useCallback(() => {
    if (!isEdit || !editInitialValues) return true;
    const comparableKeys: (keyof VfLeadFormValues)[] = [
      "name",
      "email",
      "phoneNumber",
      "job_title",
      "company_name",
      "industry",
      "how_can_verbaflo_help",
      "number_of_beds_managed",
      "number_of_properties",
      "country",
      "country_name",
      "city",
      "city_names",
      "office_location",
      "crm_currently_used",
      "pms_currently_used",
      "voip",
      "priority",
      "contact_person_name",
      "contact_person_email",
      "contact_person_designation",
      "website",
      "agent_id",
      "contract_link",
      "lead_source",
      "fathom_link",
      "demo_1_date",
      "demo_1_time",
      "demo_2_date",
      "demo_2_time",
      "demo_3_date",
      "demo_3_time",
      "demo_4_date",
      "demo_4_time",
      "timezone",
    ];
    for (const key of comparableKeys) {
      const curr = values[key];
      const init = editInitialValues[key];
      if (Array.isArray(curr) && Array.isArray(init)) {
        if (JSON.stringify(curr) !== JSON.stringify(init)) return true;
      } else if ((curr || "") !== (init || "")) {
        return true;
      }
    }
    const phoneCountryChanged =
      (values.phoneCountry?.dialCode || "") !==
      (editInitialValues.phoneCountry?.dialCode || "");
    if (phoneCountryChanged) return true;
    return false;
  }, [values, isEdit, editInitialValues]);

  const isSubmitDisabled =
    isSubmitting || isUpdating || (isEdit && !hasChanges());

  const handleInputWithDropdownChange = (
    field: string,
    val: string,
    source: "dropdown" | "input"
  ) => {
    if (source === "input") {
      const num = parseInt(val, 10);
      setFieldValue(field, !isNaN(num) && num > 0 ? String(num) : "");
    } else {
      setFieldValue(field, val);
    }
    setFieldTouched(field, true, false);
  };

  return (
    <Form className="flex flex-col h-full bg-pure-white dark:bg-pure-white-dk">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-gray dark:border-border-gray-dk">
        <div className="flex items-center gap-2">
          {isEdit ? (
            <EditUserIcon className="w-6 h-6 text-verba-blue dark:text-verba-blue-dk" />
          ) : (
            <AddUserIcon className="w-6 h-6 text-verba-blue dark:text-verba-blue-dk" />
          )}
          <span className="text-md font-semibold text-verba-indigo dark:text-verba-indigo-dk">
            {isEdit ? "Edit Lead" : "Create New Lead"}
          </span>
        </div>
        <button
          type="button"
          onClick={() => {
            resetForm();
            handleCloseDrawer();
          }}
        >
          <CloseIcon className="w-4 h-4 text-verba-indigo dark:text-verba-indigo-dk" />
        </button>
      </div>

      <div className="vf-form-scroll-container flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-4">
        <p className="text-sm font-semibold text-verba-indigo dark:text-verba-indigo-dk uppercase tracking-wide">
          Personal Info
        </p>

        <div className="flex flex-col gap-1">
          <Input
            name="name"
            label="Name"
            labelClassName={labelClass}
            value={values.name}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Enter full name"
            secondaryContainerClassName={inputContainerClass}
            inputClassName={inputClass}
            error={showErr("name")}
          />
          {showErr("name") && (
            <span className={errorClass}>{errMsg("name")}</span>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <Input
            name="email"
            label="Email"
            labelClassName={labelClass}
            type="email"
            value={values.email}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="email@example.com"
            secondaryContainerClassName={inputContainerClass}
            inputClassName={inputClass}
            error={showErr("email")}
          />
          {showErr("email") && (
            <span className={errorClass}>{errMsg("email")}</span>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <PhoneNumberCountryCodeSelector
            label="Phone Number"
            labelClassName="text-verba-field-label dark:text-verba-field-label-dk font-normal text-md"
            required={true}
            placeholder="Enter phone number"
            countryOptions={sortedPhoneCountryOptions}
            selectedCountry={values.phoneCountry}
            onCountryChange={handlePhoneCountryChange}
            phoneInput={phoneInput}
            onPhoneInputChange={handlePhoneInputChange}
            validationError={
              showErr("phoneNumber") ? errMsg("phoneNumber") : ""
            }
            containerClassName="w-full"
            countryDropdownClassName="w-[250px] min-w-[250px] max-h-[250px]"
            countryDropdownSelectedOptionClassName="h-[34px] px-[8px] py-[9px]"
            phoneInputClassName="h-[36px] px-[8px] py-[6px] rounded-[6px] bg-popper-bg dark:bg-popper-bg-dk border border-border-gray dark:border-border-gray-dk"
            countryDropdownPlaceholder="Select Country"
            countryDropdownSearchPlaceholder="Search countries..."
            countryDropdownSectionHeading="All Countries"
          />
        </div>

        <Input
          name="job_title"
          label="Job Title"
          labelClassName={labelClass}
          value={values.job_title}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="e.g. Operations Manager"
          secondaryContainerClassName={inputContainerClass}
          inputClassName={inputClass}
        />

        <div className="border-b border-border-gray dark:border-border-gray-dk" />

        <p className="text-sm font-semibold text-verba-indigo dark:text-verba-indigo-dk uppercase tracking-wide">
          Company Info
        </p>

        <div className="flex flex-col gap-1">
          <Input
            name="company_name"
            label="Company Name"
            labelClassName={labelClass}
            value={values.company_name}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Enter company name"
            secondaryContainerClassName={inputContainerClass}
            inputClassName={inputClass}
            error={showErr("company_name")}
          />
          {showErr("company_name") && (
            <span className={errorClass}>{errMsg("company_name")}</span>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <label className={labelClass}>
            Which industry do you operate in?
          </label>
          <div className="flex flex-wrap gap-2 mt-1">
            {INDUSTRY_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => {
                  setFieldValue("industry", opt.value);
                  setFieldTouched("industry", true, false);
                }}
                className={`px-3 py-1.5 rounded-lg text-md font-medium border transition-colors ${
                  values.industry === opt.value
                    ? "bg-verba-blue dark:bg-verba-blue-dk text-pure-white dark:text-pure-white-dk border-verba-blue dark:border-verba-blue-dk"
                    : "bg-pure-white dark:bg-pure-white-dk text-verba-indigo dark:text-verba-indigo-dk border-border-gray dark:border-border-gray-dk hover:border-verba-blue dark:hover:border-verba-blue-dk"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {showErr("industry") && (
            <span className={errorClass}>{errMsg("industry")}</span>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <label className={labelClass}>How can Verbaflo help?</label>
          <textarea
            name="how_can_verbaflo_help"
            value={values.how_can_verbaflo_help}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Tell us how we can help..."
            rows={3}
            className="w-full p-2 text-md border border-border-gray dark:border-border-gray-dk rounded-lg bg-pure-white dark:bg-pure-white-dk text-verba-indigo dark:text-verba-indigo-dk resize-none"
          />
        </div>

        <div className="flex gap-3">
          <div className="flex-1 flex flex-col gap-1">
            <InputWithDropdown
              id="beds-managed-dropdown"
              label="Beds Managed"
              labelClassName={labelClass}
              options={BEDS_MANAGED_OPTIONS}
              value={values.number_of_beds_managed}
              onChange={(val, source) =>
                handleInputWithDropdownChange("number_of_beds_managed", val, source)
              }
              placeholder="Select or type"
              triggerClassName="bg-pure-white dark:bg-pure-white-dk"
              className="w-full"
            />
            {showErr("number_of_beds_managed") && (
              <span className={errorClass}>
                {errMsg("number_of_beds_managed")}
              </span>
            )}
          </div>
          <div className="flex-1 flex flex-col gap-1">
            <InputWithDropdown
              id="properties-dropdown"
              label="No. of Properties"
              labelClassName={labelClass}
              options={PROPERTIES_OPTIONS}
              value={values.number_of_properties}
              onChange={(val, source) =>
                handleInputWithDropdownChange("number_of_properties", val, source)
              }
              placeholder="Select or type"
              triggerClassName="bg-pure-white dark:bg-pure-white-dk"
              className="w-full"
            />
          </div>
        </div>

        {/* <MultiSelectSearchableDropdown
          id="asset-type-multiselect"
          label="Asset Type *"
          labelClassName={labelClass}
          options={ASSET_TYPE_OPTIONS}
          value={values.asset_type}
          onChange={(vals) => {
            setFieldValue("asset_type", vals);
            setFieldTouched("asset_type", true, false);
          }}
          placeholder="Select asset type"
          searchPlaceholder="Search..."
          maxDisplayedSelections={2}
          showSearch={true}
          className="w-full gap-1!"
          placeholderClassName="text-gray-placeholder dark:text-gray-placeholder-dk text-md font-normal"
          triggerClassName="h-[36px]"
          dropdownOpenClassName="!max-h-[150px]"
        />
        {showErr("asset_type") && (
          <span className={`${errorClass} -mt-3`}>
            {errMsg("asset_type")}
          </span>
        )} */}

        <div className="flex flex-col gap-1">
          <SearchableDropdown
            id="country-dropdown"
            headerText="All Countries"
            label="Country"
            labelClassName={labelClass}
            options={destinationCountryOptions}
            value={values.country}
            onChange={(value) => {
              setFieldValue("country", value, true);
              const selectedCountry = destinationCountryOptions.find(
                (o) => o.value === value
              );
              setFieldValue(
                "country_name",
                selectedCountry?.label || "",
                false
              );
              setFieldValue("city", [], false);
              setFieldValue("city_names", [], false);
            }}
            placeholder="Select country"
            searchPlaceholder="Search country..."
            className="w-full"
            placeholderClassName="text-gray-placeholder dark:text-gray-placeholder-dk text-md font-normal"
            triggerClassName="h-[36px] w-full bg-pure-white dark:bg-pure-white-dk"
            loadingText={isCountryLoading ? "Loading countries..." : ""}
          />
          {showErr("country") && (
            <span className={errorClass}>{errMsg("country")}</span>
          )}
        </div>

        <MultiSelectSearchableDropdown
          id="city-multiselect"
          label="City"
          labelClassName={labelClass}
          options={cityOptions}
          value={values.city}
          onChange={(vals) => {
            setFieldValue("city", vals);
            const names = vals.map(
              (v: string) =>
                cityOptions.find(
                  (o: { value: string; label: string }) => o.value === v
                )?.label || v
            );
            setFieldValue("city_names", names);
            setFieldTouched("city", true, false);
          }}
          placeholder={!values.country ? "Select country first" : "Select city"}
          searchPlaceholder="Search city..."
          maxDisplayedSelections={2}
          showSearch={true}
          className="w-full gap-1!"
          placeholderClassName="text-gray-placeholder dark:text-gray-placeholder-dk text-md font-normal"
          triggerClassName="h-[36px]"
          dropdownOpenClassName="!max-h-[150px]"
          disabled={!values.country}
          loadingText={isCityLoading ? "Loading cities..." : ""}
        />

        <div className="border-b border-border-gray dark:border-border-gray-dk" />

        <p className="text-sm font-semibold text-verba-indigo dark:text-verba-indigo-dk uppercase tracking-wide">
          Client Details
        </p>

        <Input
          name="office_location"
          label="Office Location"
          labelClassName={labelClass}
          value={values.office_location}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="Enter office location"
          secondaryContainerClassName={inputContainerClass}
          inputClassName={inputClass}
        />

        <Input
          name="crm_currently_used"
          label="CRM Currently Used"
          labelClassName={labelClass}
          value={values.crm_currently_used}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="e.g. Salesforce"
          secondaryContainerClassName={inputContainerClass}
          inputClassName={inputClass}
        />

        <Dropdown
          id="pms-currently-used-dropdown"
          label="Current PMS"
          labelClassName={labelClass}
          options={pmsOptions}
          value={values.pms_currently_used || null}
          onChange={(value) => {
            setFieldValue("pms_currently_used", value);
          }}
          placeholder="Select PMS"
          placeholderClassName="text-gray-placeholder dark:text-gray-placeholder-dk text-md font-normal"
          selectedOptionContainerClassName="w-full"
          className="w-full"
          triggerClassName="w-full bg-pure-white dark:bg-pure-white-dk"
          dropdownMenuTitle={
            <PmsOtherHeader
              isOtherPms={isOtherPms}
              setIsOtherPms={setIsOtherPms}
              customPmsValue={customPmsValue}
              setCustomPmsValue={setCustomPmsValue}
              onSubmit={handleCustomPmsSubmit}
            />
          }
        />

        <Input
          name="voip"
          label="VoIP"
          labelClassName={labelClass}
          value={values.voip}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="e.g. Twilio"
          secondaryContainerClassName={inputContainerClass}
          inputClassName={inputClass}
        />

        

        <div className="flex flex-col gap-2">
          <SearchableDropdown
            id="demo-timezone-dropdown"
            options={timezoneOptions}
            value={values.timezone}
            onChange={(value) => {
              setFieldValue("timezone", value);
              setFieldTouched("timezone", true, false);
            }}
            placeholder="Select a timezone"
            sectionHeading="All Timezones"
            selectedOptionContainerClassName="w-full"
            placeholderClassName="text-gray-placeholder dark:text-gray-placeholder-dk text-md font-normal"
            className="w-full"
            Icon={NetworkIcon}
            iconClassName="w-[18px] h-[18px] text-verba-indigo dark:text-verba-indigo-dk"
            triggerClassName="w-full bg-pure-white dark:bg-pure-white-dk"
            label="Demo Timezone"
            labelClassName={labelClass}
          />
        </div>

        <div className="grid grid-cols-1 gap-3">
          {(
            [
              { dateField: "demo_1_date", timeField: "demo_1_time", idx: 1 },
              { dateField: "demo_2_date", timeField: "demo_2_time", idx: 2 },
              { dateField: "demo_3_date", timeField: "demo_3_time", idx: 3 },
              { dateField: "demo_4_date", timeField: "demo_4_time", idx: 4 },
            ] as const
          ).map(({ dateField, timeField, idx }) => (
            <div key={dateField} className="flex flex-col gap-1">
              <label className={labelClass}>{`Demo ${idx} Date & Time`}</label>
              <div className="flex items-center gap-2">
                <SingleDatePicker
                  value={values[dateField] || null}
                  Icon={CalendarIcon}
                  onChange={(date) => {
                    if (date) {
                      const d = new Date(date);
                      const year = d.getFullYear();
                      const month = String(d.getMonth() + 1).padStart(2, "0");
                      const day = String(d.getDate()).padStart(2, "0");
                      setFieldValue(dateField, `${year}-${month}-${day}`);
                    } else {
                      setFieldValue(dateField, "");
                    }
                  }}
                  minDate={new Date()}
                  placeholder="Select date"
                  scrollIntoView={true}
                  containerClassName="flex-1"
                  iconClassName="w-[18px] h-[18px] text-gray-placeholder dark:text-gray-placeholder-dk"
                  inputClassName="w-full h-full text-md font-normal px-[10px] py-[7px] rounded-[8px] bg-popper-bg dark:bg-popper-bg-dk border border-border-gray dark:border-border-gray-dk"
                />
                <TimePicker
                  value={values[timeField]}
                  onChange={(time) => setFieldValue(timeField, time)}
                  placeholder="Time"
                  className="w-[120px]"
                  Icon={ClockIcon}
                  iconClassName="w-3 h-3 text-gray-placeholder dark:text-gray-placeholder-dk"
                  selectedOptionContainerClassName="w-full h-[36px] py-0 !gap-0 !px-3"
                  placeholderClassName="text-md font-normal placeholder:text-gray-placeholder dark:placeholder:text-gray-placeholder-dk"
                  optionClassName="text-md font-normal text-heading-text dark:text-heading-text-dk border-none"
                  triggerClassName="h-full bg-pure-white dark:bg-pure-white-dk"
                />
              </div>
              {values.timezone && values[dateField] && values[timeField] && (
                <span className="text-xxs text-dark-gray dark:text-dark-gray-dk">
                  <span className="font-semibold text-verba-field-label dark:text-verba-field-label-dk">
                    Local time:
                  </span>{" "}
                  {`${formatAsLocalTime(
                    values[dateField],
                    values[timeField],
                    values.timezone
                  )} (${Intl.DateTimeFormat().resolvedOptions().timeZone})`}
                </span>
              )}
            </div>
          ))}
        </div>

        <div className="flex flex-col gap-1">
          <Dropdown
            id="priority-dropdown"
            label="Priority"
            labelClassName={labelClass}
            options={PRIORITY_OPTIONS}
            value={values.priority}
            onChange={(value) => {
              setFieldValue("priority", value);
              setFieldTouched("priority", true, false);
            }}
            placeholder="Select priority"
            placeholderClassName="text-gray-placeholder dark:text-gray-placeholder-dk text-md font-normal"
            selectedOptionContainerClassName="w-full"
            className="w-full"
            triggerClassName="w-full bg-pure-white dark:bg-pure-white-dk"
          />
          {showErr("priority") && (
            <span className={errorClass}>{errMsg("priority")}</span>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <Input
            name="contact_person_name"
            label="Contact Person Name"
            labelClassName={labelClass}
            value={values.contact_person_name}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Enter contact name"
            secondaryContainerClassName={inputContainerClass}
            inputClassName={inputClass}
            error={showErr("contact_person_name")}
          />
          {showErr("contact_person_name") && (
            <span className={errorClass}>{errMsg("contact_person_name")}</span>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <Input
            name="contact_person_email"
            label="Contact Person Email"
            labelClassName={labelClass}
            type="email"
            value={values.contact_person_email}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Enter contact email"
            secondaryContainerClassName={inputContainerClass}
            inputClassName={inputClass}
            error={showErr("contact_person_email")}
          />
          {showErr("contact_person_email") && (
            <span className={errorClass}>{errMsg("contact_person_email")}</span>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <Input
            name="contact_person_designation"
            label="Contact Person Designation"
            labelClassName={labelClass}
            value={values.contact_person_designation}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Enter designation"
            secondaryContainerClassName={inputContainerClass}
            inputClassName={inputClass}
            error={showErr("contact_person_designation")}
          />
          {showErr("contact_person_designation") && (
            <span className={errorClass}>
              {errMsg("contact_person_designation")}
            </span>
          )}
        </div>

        <div className="border-b border-border-gray dark:border-border-gray-dk" />

        <p className="text-sm font-semibold text-verba-indigo dark:text-verba-indigo-dk uppercase tracking-wide">
          Additional
        </p>

        <div className="flex flex-col gap-1">
          <Input
            name="website"
            label="Website"
            labelClassName={labelClass}
            value={values.website}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="https://example.com"
            secondaryContainerClassName={inputContainerClass}
            inputClassName={inputClass}
            error={showErr("website")}
          />
          {showErr("website") && (
            <span className={errorClass}>{errMsg("website")}</span>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <AgentDropdown
            selectedAgentIds={values.agent_id}
            onSelect={(agentIds) => {
              setFieldValue("agent_id", agentIds);
              setFieldTouched("agent_id", true, false);
            }}
            maxAgents={2}
            label="Assignee"
          />
          {showErr("agent_id") && (
            <span className={errorClass}>{errMsg("agent_id")}</span>
          )}
        </div>

        <Input
          name="contract_link"
          label="Contract Link"
          labelClassName={labelClass}
          value={values.contract_link}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="Google Drive link"
          secondaryContainerClassName={inputContainerClass}
          inputClassName={inputClass}
        />

        <Input
          name="lead_source"
          label="Lead Source"
          labelClassName={labelClass}
          value={values.lead_source}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="e.g. Referral, Website"
          secondaryContainerClassName={inputContainerClass}
          inputClassName={inputClass}
        />

        <Input
          name="fathom_link"
          label="Fathom Link"
          labelClassName={labelClass}
          value={values.fathom_link}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="Fathom meeting link"
          secondaryContainerClassName={inputContainerClass}
          inputClassName={inputClass}
        />
      </div>

      <div className="flex items-center justify-end gap-3 px-4 py-3 border-t border-border-gray dark:border-border-gray-dk">
        <Button
          innerText="Cancel"
          onClick={() => {
            resetForm();
            handleCloseDrawer();
          }}
          buttonContainerClass="py-2 px-6 border border-verba-indigo dark:border-verba-indigo-dk rounded-lg"
          innerTextClass="text-md font-medium text-heading-text dark:text-heading-text-dk"
        />
        <Button
          innerText={
            isUpdating
              ? isEdit
                ? "Updating..."
                : "Creating..."
              : isEdit
                ? "Update Lead"
                : "Create Lead"
          }
          type="submit"
          disabled={isSubmitDisabled}
          buttonContainerClass="py-2 px-6 rounded-lg bg-verba-indigo dark:bg-verba-indigo-dk disabled:opacity-50 disabled:cursor-not-allowed"
          innerTextClass="text-md font-medium text-pure-white dark:text-pure-white-dk"
        />
      </div>
    </Form>
  );
};

export default VfCreateLeadForm;
